import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, ShoppingBag, UtensilsCrossed, TrendingUp } from "lucide-react";
import type { Restaurant, DbProduct, DbOrder } from "@/hooks/useRestaurant";

type Props = {
  orders: DbOrder[];
  menuItems: DbProduct[];
  restaurant: Restaurant | null;
};

export default function AdminOverview({ orders, menuItems, restaurant }: Props) {
  const totalRevenue = orders.filter(o => o.status !== "cancelled").reduce((sum, o) => sum + Number(o.total), 0);
  const activeOrders = orders.filter(o => !["completed", "cancelled"].includes(o.status)).length;

  const stats = [
    { label: "Total Revenue", value: `${restaurant?.currency_symbol || "$"}${totalRevenue.toFixed(2)}`, icon: DollarSign, color: "text-green-500" },
    { label: "Total Orders", value: orders.length, icon: ShoppingBag, color: "text-primary" },
    { label: "Active Orders", value: activeOrders, icon: TrendingUp, color: "text-orange-500" },
    { label: "Products", value: menuItems.length, icon: UtensilsCrossed, color: "text-blue-500" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{s.label}</CardTitle>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <p className="text-muted-foreground text-sm">No orders yet. Share your menu link to start receiving orders!</p>
          ) : (
            <div className="space-y-3">
              {orders.slice(0, 5).map((order) => (
                <div key={order.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div>
                    <span className="font-medium">{order.order_number}</span>
                    <span className="text-muted-foreground text-sm ml-2">{order.order_type}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      order.status === "completed" ? "bg-green-100 text-green-700" :
                      order.status === "cancelled" ? "bg-red-100 text-red-700" :
                      "bg-orange-100 text-orange-700"
                    }`}>
                      {order.status}
                    </span>
                    <span className="font-semibold">{restaurant?.currency_symbol}{Number(order.total).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
